import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import React from "react";
import { Modal } from "react-bootstrap";

// Icons
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import PhoneIcon from "@mui/icons-material/Phone";
import SecurityIcon from "@mui/icons-material/Security";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import WorkIcon from "@mui/icons-material/Work";

// Composant stylisé pour les puces de statut
const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.success.light
    : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  boxShadow: "0 2px 5px 0 rgba(0,0,0,0.08)",
}));

// Composant pour afficher une ligne d'information avec une icône
const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2.5 }}>
    <Box
      sx={{
        mr: 2,
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "rgba(55, 176, 73, 0.1)",
        color: "#37B049",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, letterSpacing: 0.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || "Non renseigné"}
      </Typography>
    </Box>
  </Box>
);

const UserProfil = ({ show, handleClose, user }) => {
  if (!user) {
    return null;
  }

  const {
    id,
    nom,
    prenom,
    matricule,
    email,
    tel,
    role,
    adresse,
    date_naissance,
    statut,
    genre,
    createdAt,
    image,
    type_document_id,
    numero_document_id,
    date_delivre,
    lieu_delivre,
    departement,
    fonction,
  } = user;

  // Fonction pour mettre en forme la date
  const formatDate = (date) => {
    if (!date) return null;
    return dayjs(date).format("DD/MM/YYYY");
  };

  // Générer les initiales à partir du nom et prénom
  const getInitials = () => {
    return `${nom?.charAt(0) || ""}${prenom?.charAt(0) || ""}`.toUpperCase();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="user-profile-modal"
      centered
      backdrop="static"
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, color: "#2e7d32" }}
        >
          Profil Utilisateur
        </Typography>
      </Modal.Header>

      <Modal.Body>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            background:
              "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
          }}
        >
          {/* En-tête du profil avec image et informations principales */}
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-start" },
              gap: 4,
              position: "relative",
            }}
          >
            {/* Colonne de gauche - Photo et infos principales */}
            <Fade in={true} timeout={800}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Avatar
                  src={image}
                  alt={`${nom} ${prenom}`}
                  sx={{
                    width: 130,
                    height: 130,
                    mb: 2,
                    fontSize: "2.5rem",
                    bgcolor: "#37B049",
                    border: "4px solid white",
                    boxShadow: "0 4px 14px rgba(55, 176, 73, 0.2)",
                  }}
                >
                  {getInitials()}
                </Avatar>

                <StatusChip
                  label={statut ? "Actif" : "Inactif"}
                  active={statut}
                  sx={{ mt: 1, mb: 1, borderRadius: "16px", px: 1 }}
                  size="medium"
                />
              </Box>
            </Fade>

            {/* Colonne de droite - Identité */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#37B049",
                }}
              >
                {nom} {prenom}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {role && (
                  <Chip
                    icon={<SupervisorAccountIcon />}
                    label={role}
                    variant="outlined"
                    color="success"
                    sx={{ fontWeight: 500 }}
                  />
                )}

                {fonction && (
                  <Chip
                    icon={<WorkIcon />}
                    label={fonction}
                    variant="outlined"
                    color="primary"
                    sx={{ fontWeight: 500 }}
                  />
                )}

                {departement && (
                  <Chip
                    icon={<LocationOnIcon />}
                    label={departement}
                    variant="outlined"
                    color="info"
                    sx={{ fontWeight: 500 }}
                  />
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <InfoRow
                  icon={<BadgeIcon fontSize="small" />}
                  label="Matricule"
                  value={matricule}
                />
                <InfoRow
                  icon={<EmailIcon fontSize="small" />}
                  label="Email"
                  value={email}
                />
                <InfoRow
                  icon={<PhoneIcon fontSize="small" />}
                  label="Téléphone"
                  value={tel}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mx: 3 }} />

          {/* Détails utilisateur */}
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 3, color: "#2e7d32" }}
            >
              Informations Personnelles
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              {/* Colonne gauche */}
              <Box sx={{ flex: "1 1 300px", pr: { xs: 0, md: 3 }, mb: 3 }}>
                <InfoRow
                  icon={<PersonIcon fontSize="small" />}
                  label="Identité"
                  value={`${nom} ${prenom}`}
                />
                <InfoRow
                  icon={<PersonPinIcon fontSize="small" />}
                  label="Genre"
                  value={genre}
                />
                <InfoRow
                  icon={<CalendarTodayIcon fontSize="small" />}
                  label="Date de naissance"
                  value={formatDate(date_naissance)}
                />
                <InfoRow
                  icon={<HomeIcon fontSize="small" />}
                  label="Adresse"
                  value={adresse}
                />
              </Box>

              {/* Colonne droite */}
              <Box sx={{ flex: "1 1 300px" }}>
                <InfoRow
                  icon={<SecurityIcon fontSize="small" />}
                  label="Type de document"
                  value={type_document_id}
                />
                <InfoRow
                  icon={<BadgeIcon fontSize="small" />}
                  label="Numéro de document"
                  value={numero_document_id}
                />
                <InfoRow
                  icon={<CalendarTodayIcon fontSize="small" />}
                  label="Date de délivrance"
                  value={formatDate(date_delivre)}
                />
                <InfoRow
                  icon={<LocationOnIcon fontSize="small" />}
                  label="Lieu de délivrance"
                  value={lieu_delivre}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Modal.Body>

      <Modal.Footer style={{ border: "none", paddingTop: 0 }}>
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            backgroundColor: "#37B049",
            boxShadow: "0 4px 12px rgba(55, 176, 73, 0.3)",
            "&:hover": {
              backgroundColor: "#2e8b3a",
              boxShadow: "0 6px 14px rgba(55, 176, 73, 0.4)",
            },
            px: 4,
            py: 1,
          }}
        >
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserProfil;
